// HeroCarousel.jsx
import { useEffect, useRef, useState, useCallback } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import styles from './SecondScreen.module.css';

// Desktop images (WebP for better performance)
import heroImage2 from '../images/תמונות פתיח חדשות חן 2.webp';
import heroImage3 from '../images/תמונות פתיח חדשות חן 3.webp';
import heroImage4 from '../images/תמונות פתיח חדשות חן 4.webp';
import heroImage5 from '../images/תמונות פתיח חדשות חן 5.webp';
import heroImage6 from '../images/תמונות פתיח חדשות חן 7.webp';

// Mobile image (portrait resolution, WebP)
import mobileImage1 from '../images/תמונות פתיח חדשות חן 6.webp';

gsap.registerPlugin(ScrollTrigger);

// Loading Animation Component - Logo draw-in
const LoadingAnimation = () => {
  return (
    <div className={styles.loadingContainer}>
      <div className={styles.loadingScene}>
        <svg
          className={styles.logoSvg}
          viewBox="0 0 300 260"
          fill="none"
        >
          {/* Outer rectangle */}
          <rect
            className={styles.logoOuter}
            x="70" y="16" width="190" height="190"
            stroke="#1a365d" strokeWidth="2.2" fill="none"
          />
          {/* Inner rectangle - offset */}
          <rect
            className={styles.logoInner}
            x="110" y="56" width="110" height="130"
            stroke="#1a365d" strokeWidth="2" fill="none"
          />
          {/* Diagonal slash */}
          <line
            className={styles.logoDiagonal}
            x1="42" y1="196" x2="262" y2="30"
            stroke="#1a365d" strokeWidth="2"
          />
          {/* Vertical structure lines */}
          <line className={styles.logoLine1} x1="150" y1="56" x2="150" y2="186" stroke="#1a365d" strokeWidth="1.8" />
          <line className={styles.logoLine2} x1="185" y1="100" x2="185" y2="186" stroke="#1a365d" strokeWidth="1.8" />
          {/* Horizontal structure line */}
          <line className={styles.logoLine3} x1="110" y1="130" x2="185" y2="130" stroke="#1a365d" strokeWidth="1.8" />
        </svg>

        <div className={styles.loadingBrand}>
          <span className={styles.loadingBrandName}>AFRIAT ARCHITECTS</span>
        </div>

        <div className={styles.loadingProgress}>
          <div className={styles.loadingProgressBar} />
        </div>
      </div>
    </div>
  );
};

const HeroCarousel = () => {
  const heroRef = useRef(null);
  const imagesRef = useRef([]);
  const contentRef = useRef(null);
  const subtitleRef = useRef(null);
  const descriptionRef = useRef(null);
  const statsRef = useRef(null);
  const statItemsRef = useRef([]);
  const zoomTweenRef = useRef(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset carousel when switching between mobile/desktop
  useEffect(() => {
    setCurrentIndex(0);
    setIsTransitioning(false);
  }, [isMobile]);

  const desktopImages = [
    heroImage2,
    heroImage3,
    heroImage4,
    heroImage5,
    heroImage6,
  ];

  const mobileImages = [
    mobileImage1,
    heroImage2,
    heroImage3,
    heroImage4,
    heroImage5,
  ];

  const images = isMobile ? mobileImages : desktopImages;

  const startZoom = useCallback((index) => {
    if (zoomTweenRef.current) {
      zoomTweenRef.current.kill();
    }
    const imgEl = imagesRef.current[index];
    if (!imgEl) return;
    gsap.set(imgEl, { scale: 1.12 });
    zoomTweenRef.current = gsap.to(imgEl, {
      scale: 1,
      duration: 6,
      ease: 'none',
    });
  }, []);

  // Preload all images - loading shows until ready
  useEffect(() => {
    const promises = images.map((src) => {
      return new Promise((res) => {
        const img = new Image();
        img.onload = res;
        img.onerror = res;
        img.src = src;
      });
    });

    Promise.all(promises).then(() => {
      setIsLoading(false);
      // Refresh all ScrollTriggers after hero content appears
      // Multiple refreshes to handle fonts loading and layout settling
      ScrollTrigger.refresh();
      setTimeout(() => ScrollTrigger.refresh(), 300);
      setTimeout(() => ScrollTrigger.refresh(), 1000);
      setTimeout(() => ScrollTrigger.refresh(), 2500);
      // Also refresh once fonts are fully loaded
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          ScrollTrigger.refresh();
          setTimeout(() => ScrollTrigger.refresh(), 200);
        });
      }
    });
  }, []);

  const word = 'AFRIAT';
  const wordSub = 'ARCHITECTS';
  const subtitle = 'תכנון ללא פשרות';
  const description = 'משרד בוטיק לאדריכלות ועיצוב פנים';

  const stats = [
    { number: '10+', label: 'שנות ניסיון בשטח' },
    { number: '3D', label: 'שליטה מלאה בתלת-ממד' },
    { number: '100%', label: 'וודאות עד לתוצאה מושלמת' },
  ];

  // Magazine-style image transition
  const transitionToImage = (nextIndex) => {
    if (isTransitioning || nextIndex === currentIndex) return;
    
    setIsTransitioning(true);
    
    const currentImage = imagesRef.current[currentIndex];
    const nextImage = imagesRef.current[nextIndex];
    
    // Magazine wipe/reveal effect
    const tl = gsap.timeline({
      onComplete: () => {
        setCurrentIndex(nextIndex);
        setIsTransitioning(false);
      }
    });

    // Set next image ready - already zoomed in
    gsap.set(nextImage, {
      clipPath: 'inset(0 100% 0 0)',
      opacity: 1,
      scale: 1.12,
      zIndex: 2
    });

    // Start zoom-out on new image immediately (runs during the wipe)
    startZoom(nextIndex);

    // Reveal next image with wipe
    tl.to(nextImage, {
      clipPath: 'inset(0 0% 0 0)',
      duration: 1.2,
      ease: 'power3.inOut',
    }, 0);

    // Reset current after transition
    tl.set(currentImage, {
      opacity: 0,
      scale: 1,
      zIndex: 1
    });

    tl.set(nextImage, {
      zIndex: 1
    });
  };

  // Auto-advance carousel
  useEffect(() => {
    if (isLoading) return;
    const interval = setInterval(() => {
      const nextIndex = (currentIndex + 1) % images.length;
      transitionToImage(nextIndex);
    }, 4500);

    return () => clearInterval(interval);
  }, [currentIndex, isTransitioning, isLoading]);

  // Scroll-triggered text reveals
  useEffect(() => {
    if (isLoading) return;
    const ctx = gsap.context(() => {
      // Subtle parallax on scroll for the entire hero
      gsap.to(heroRef.current, {
        scrollTrigger: {
          trigger: heroRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        },
        y: 100,
        ease: 'none',
      });

      // Reveal subtitle
      gsap.fromTo(subtitleRef.current,
        { 
          opacity: 0, 
          y: 40,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: heroRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Reveal description with slight delay
      gsap.fromTo(descriptionRef.current,
        { 
          opacity: 0, 
          y: 30,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: heroRef.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Reveal stats one by one
      statItemsRef.current.forEach((stat, index) => {
        gsap.fromTo(stat,
          { 
            opacity: 0, 
            y: 25,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: heroRef.current,
              start: `top ${75 - index * 5}%`,
              toggleActions: 'play none none reverse',
            },
          }
        );
      });
    });

    return () => ctx.revert();
  }, [isLoading]);

  // Initialize first image + start zoom-out
  useEffect(() => {
    if (isLoading) return;
    imagesRef.current.forEach((img, index) => {
      if (index === 0) {
        gsap.set(img, { opacity: 1 });
      } else {
        gsap.set(img, { opacity: 0 });
      }
    });
    startZoom(0);
  }, [isLoading, startZoom]);

  return (
    <section className={styles.hero} ref={heroRef}>
      {/* Loading overlay - inside hero, disappears when images ready */}
      {isLoading && <LoadingAnimation />}

      {/* Hero content - appears when images loaded */}
      {!isLoading && (
        <>
          {/* Image carousel */}
          <div className={styles.imagesContainer}>
            {images.map((src, index) => (
              <img
                key={index}
                src={src}
                alt={`אפריאט אדריכלות - פרויקט ${index + 1}`}
                className={styles.backgroundImage}
                ref={(el) => (imagesRef.current[index] = el)}
                fetchPriority={index === 0 ? 'high' : 'low'}
                decoding={index === 0 ? 'sync' : 'async'}
              />
            ))}
          </div>

          {/* Overlay */}
          <div className={styles.overlay} />

          {/* Vignette effect */}
          <div className={styles.vignette} />

          {/* AFRIAT ARCHITECTS - visible immediately */}
          <div className={styles.lettersContainer}>
            <div className={styles.lettersRow}>
              {word.split('').map((char, index) => (
                <span
                  key={index}
                  className={styles.letter}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {char}
                </span>
              ))}
            </div>
            <span
              className={styles.lettersSub}
              style={{ animationDelay: `${word.length * 0.1 + 0.2}s` }}
            >
              {wordSub}
            </span>
          </div>

          {/* Content - scroll reveal */}
          <div className={styles.content} ref={contentRef}>
            <h2 className={styles.subtitle} ref={subtitleRef}>
              {subtitle}<br />ביצוע ללא טעויות
            </h2>
            <p className={styles.description} ref={descriptionRef}>
              {description}
            </p>
            <a
              href="https://wa.me/972548145494?text=%D7%94%D7%99%D7%99%20%D7%97%D7%9F%2C%20%D7%94%D7%92%D7%A2%D7%AA%D7%99%20%D7%9E%D7%94%D7%90%D7%AA%D7%A8%20%D7%A9%D7%9C%D7%9A%2C%20%D7%90%D7%A9%D7%9E%D7%97%20%D7%9C%D7%A9%D7%9E%D7%95%D7%A2%20%D7%A2%D7%95%D7%93%20%D7%A2%D7%9C.."
              target="_blank"
              rel="noopener noreferrer"
              className={styles.glassCta}
            >
              ליצירת קשר לחצו כאן
            </a>
          </div>

          {/* Stats - scroll reveal */}
          <div className={styles.stats} ref={statsRef}>
            {stats.map((stat, index) => (
              <div
                key={index}
                className={styles.statItem}
                ref={(el) => (statItemsRef.current[index] = el)}
              >
                <span className={styles.statNumber}>{stat.number}</span>
                <span className={styles.statLabel}>{stat.label}</span>
              </div>
            ))}
          </div>

          {/* Carousel indicators */}
          <div className={styles.indicators}>
            {images.map((_, index) => (
              <button
                key={index}
                className={`${styles.indicator} ${index === currentIndex ? styles.active : ''}`}
                onClick={() => transitionToImage(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Scroll hint */}
          <div className={styles.scrollHint}>
            <span className={styles.scrollText}>גלול למטה</span>
            <div className={styles.scrollLine} />
          </div>
        </>
      )}
    </section>
  );
};

export default HeroCarousel;